import { useIsFocused, useNavigation } from '@react-navigation/native';

import { forceMobileViewAtom } from 'app/atoms/ui';
import { DASHBOARD_LAYOUT_MAX_WIDTH } from 'app/components/web/dashboard-layout-constants';
import type { SpecificNavigationProp } from 'app/types/navigation';
import Constants from 'expo-constants';
import { getDefaultStore, useAtomValue } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, useWindowDimensions } from 'react-native';
import { isMobileNative } from './constants';
import { isLoggedInAtom } from 'app/atoms';
import { FeatureFlag, isFeatureEnabled, isWhiteLabel } from './helpers';
import { env } from 'app/lib/env';

export { isMobileNative } from './constants';

export function getIsLargeUI() {
  if (isMobileNative) {
    return false;
  }
  return !getDefaultStore().get(forceMobileViewAtom);
}

export const useUISize = () => {
  const forceMobileView = useAtomValue(forceMobileViewAtom);
  const isSmallUI = isMobileNative || forceMobileView;
  return { isSmallUI, isLargeUI: !isSmallUI };
};

export const isGuestAccessEnabled = () =>
  !isWhiteLabel() && (env.IS_PARTAKE || isFeatureEnabled(FeatureFlag.guestAccess));

export const useIsGuestWeb = () => {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  return Platform.OS === 'web' && !isLoggedIn && isGuestAccessEnabled();
};

// In desktop (large UI) view, the maximum width (of BottomSheets) is set to 3/5 of the screen height
export const useMaxWidth = () => {
  const browserWidth = useWindowDimensions().width;
  return useUISize().isSmallUI ? '100%' : Math.min(DASHBOARD_LAYOUT_MAX_WIDTH * 0.35, (browserWidth * 3) / 5);
};

export function useDeviceMaxWidth() {
  const nativeWindowWidth = useWindowDimensions().width;
  const [webScreenWidth, setWebScreenWidth] = useState(() => (isMobileNative ? 0 : window.screen.width));

  useEffect(() => {
    if (isMobileNative) return;

    const handleResize = () => setWebScreenWidth(window.screen.width);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobileNative ? nativeWindowWidth : webScreenWidth;
}

export const useBreakpoint = (breakpoint = 768, entireScreen = false) => {
  const screenWidth = useDeviceMaxWidth();
  const windowWidth = useWindowDimensions().width;
  const width = entireScreen ? screenWidth : windowWidth;
  const isAboveBreakpoint = width >= breakpoint;
  return isAboveBreakpoint;
};

export const useAlignmentIfDesktop = () => {
  const { isLargeUI } = useUISize();
  return isLargeUI ? 'left' : 'center';
};

export const usePageTitle = (ns?: string, navigation?: SpecificNavigationProp) => {
  const defaultNavigation = useNavigation();
  const resolvedNavigation = navigation ?? (defaultNavigation as unknown as SpecificNavigationProp);
  const isFocused = useIsFocused();
  const { t } = useTranslation(ns);
  return useCallback(
    (key: string, values: Record<string, string | undefined | null> = {}) => {
      // React Navigation mounts preloaded routes with placeholder navigation.
      // Placeholder screens are never focused and throw when setOptions is called.
      if (!isFocused) return;

      resolvedNavigation.setOptions({
        title: `${t(key, values)} | ${Constants.expoConfig?.extra?.APP_NAME as string}`,
      });
    },
    [isFocused, resolvedNavigation, t],
  );
};

export { getWebWindow } from './web-window';
